import { usePurchaserId } from '@openeducation/hr-rn-cognito-providers';
import {
  HRHeading,
  HRLicensesOverview,
  HRLicensesList,
  HRContactUsModal,
  HRAssignReassignLicenseModal,
  useHRPortalTranslation,
  useAssignReassignLicense,
  useLicensesList,
  useContactUs,
  useReport,
  SloganBanner,
  mb,
  HRModal,
  borderRadius,
} from '@openeducation/hr-rn-components';
import { useGetLicensesOverview } from '@openeducation/hr-rn-graphql-providers';
import {
  hrPortalConfigAtom,
  nextUSimplifiedAtom,
} from '@openeducation/hr-rn-jotai-atoms';
import {
  StudentsListField,
  ReportType,
} from '@openeducation/hr-rn-shared-types';
import type { StudentsListItemData } from '@openeducation/hr-rn-shared-types';
import { styled, useStyledTheme } from '@openeducation/hr-rn-themes';
import { useAtomValue } from 'jotai';
import React, { useState, useEffect, useCallback } from 'react';
import { View } from 'react-native';

import { saveFile } from '../../helpers/save-file';
import { useStudentsListProps } from '../../hooks/use-students-list-props';
import { NUPath } from '../../router';

enum VisibleModal {
  CONTACT_US = 'CONTACT_US',
  CONTACT_US_SUCCESS = 'CONTACT_US_SUCCESS',
}

const NULicensesScreen = () => {
  const [visibleModal, setVisibleModal] = useState<VisibleModal | null>(null);
  const { t } = useHRPortalTranslation();
  const theme = useStyledTheme();
  const purchaserId = usePurchaserId();
  const isNextUSimplified = useAtomValue(nextUSimplifiedAtom);
  const {
    moreLicensesEmailSubject,
    organization,
    buttonTextColor,
    successMessageBackgroundColor,
    successMessageTextColor,
  } = useAtomValue(hrPortalConfigAtom);
  const { data: overviewData, loading: overviewLoading } =
    useGetLicensesOverview({
      purchaserId,
      organization,
    });
  const contactUsProps = useContactUs({
    subject: moreLicensesEmailSubject,
  });
  const {
    data: listData,
    isLoading: listLoading,
    refetchListData,
  } = useLicensesList();
  const studentsListProps = useStudentsListProps(NUPath.Student);
  const licensesReport = useReport(saveFile, ReportType.LICENSES);
  const { openAssignReassign, modalAssignReassignLicenseProps } =
    useAssignReassignLicense();
  useEffect(() => {
    if (contactUsProps?.isSuccess) {
      setVisibleModal(VisibleModal.CONTACT_US_SUCCESS);
    }
  }, [contactUsProps?.isSuccess]);

  const closeModals = () => setVisibleModal(null);
  const openContactModal = () => {
    setVisibleModal(VisibleModal.CONTACT_US);
  };
  const onAssignReassign = useCallback(
    (license: StudentsListItemData) => {
      openAssignReassign(license);
    },
    [openAssignReassign]
  );
  const licenseFields = isNextUSimplified
    ? [
        StudentsListField.NAME,
        StudentsListField.EMAIL,
        StudentsListField.STATUS,
        StudentsListField.EXPIRATION,
        StudentsListField.ASSIGN_REASSIGN,
      ]
    : [
        StudentsListField.NAME,
        StudentsListField.EMAIL,
        StudentsListField.PLAN_TYPE,
        StudentsListField.STATUS,
        StudentsListField.EXPIRATION,
        StudentsListField.ASSIGN_REASSIGN,
      ];
  return (
    <>
      <HRHeading
        title={t('licenses.heading.title')}
        description={t('licenses.heading.description')}
        studentsListProps={studentsListProps}
        licensesReport={licensesReport}
      />
      <HRLicensesOverview
        isLoading={overviewLoading}
        data={overviewData}
        onMoreLicensesPress={openContactModal}
      />
      <NULicensesList
        data={listData}
        isLoading={listLoading}
        refetchListData={refetchListData}
        fields={licenseFields}
        onAssignReassign={onAssignReassign}
        disabledAssignFlow={isNextUSimplified}
      />
      <HRAssignReassignLicenseModal {...modalAssignReassignLicenseProps} />
      <View style={[mb(40)]}>
        <Banner
          text={t('licenses.slogan')}
          gradientColors={[theme.color.nuBlue, theme.color.nuBlue]}
          button={{
            onPress: openContactModal,
            text: t('licenses.overview.moreLicenses'),
            style: borderRadius(4),
            color: theme.color.primary,
            labelStyle: buttonTextColor ? { color: buttonTextColor } : {},
          }}
        />
      </View>
      <HRContactUsModal
        title={t('getLicenses.title')}
        description={t('getLicenses.description')}
        visible={visibleModal === VisibleModal.CONTACT_US}
        onCancel={closeModals}
        isLoading={contactUsProps?.isLoading}
        error={contactUsProps?.error}
        onSubmit={contactUsProps?.onSubmit}
        managerName={contactUsProps?.managerName}
        managerEmail={contactUsProps?.managerEmail}
      />
      <HRModal
        visible={visibleModal === VisibleModal.CONTACT_US_SUCCESS}
        onClose={closeModals}
        successMessage={{
          message: t('getLicenses.success.title'),
          backgroundColor: successMessageBackgroundColor,
        }}
        successMessageSecondary={{
          message: t('getLicenses.success.description'),

          color: successMessageTextColor,
        }}
        autoDismiss
      />
    </>
  );
};

const NULicensesList = styled(HRLicensesList)`
  border-width: 1px;
  border-color: ${({ theme }) => theme.color.border};
`;

const Banner = styled(SloganBanner)`
  border-radius: 8px;
`;

export default NULicensesScreen;
